import { StreamId, StreamSpecs } from "@common/domain";
import { BehaviorSubject, firstValueFrom, Observable, of } from "rxjs";
import { mergeMap, switchMap } from "rxjs/operators";
import { ExtendedMediaStream } from "../../extended-media-stream";

export abstract class BaseConferenceStream {
  protected _stream$ = new BehaviorSubject<null | ExtendedMediaStream>(null);

  protected constructor(protected readonly _streamId: StreamId) {}

  get stream$(): Observable<null | ExtendedMediaStream> {
    return this._stream$.asObservable();
  }

  get streamId() {
    return this._streamId;
  }

  get isVideoEnabled$(): Observable<boolean> {
    return this._stream$.pipe(mergeMap((_) => (_ ? _.isVideoEnabled$ : [])));
  }

  get isAudioEnabled$(): Observable<boolean> {
    return this._stream$.pipe(mergeMap((_) => (_ ? _.isAudioEnabled$ : [])));
  }

  get isAudioFile() {
    return StreamSpecs.isOfType(this._streamId, "localAudioFile");
  }

  get isCamMic() {
    return StreamSpecs.isOfType(this._streamId, "camMic");
  }

  get isScreen() {
    return StreamSpecs.isOfType(this._streamId, "screen");
  }

  get isVideoFile() {
    return StreamSpecs.isOfType(this._streamId, "localVideoFile");
  }

  get videoTracks$() {
    return this._stream$.pipe(switchMap((_) => _?.videoTracks$ || of([])));
  }

  get audioTracks$() {
    return this._stream$.pipe(switchMap((_) => _?.audioTracks$ || of([])));
  }

  protected async audioDisable() {
    const stream = await firstValueFrom(this._stream$);

    stream?.audioDisable();
  }

  protected async audioEnable() {
    const stream = await firstValueFrom(this._stream$);

    stream?.audioEnable();
  }

  protected async videoDisable() {
    const stream = await firstValueFrom(this._stream$);

    stream?.videoDisable();
  }

  protected async videoEnable() {
    const stream = await firstValueFrom(this._stream$);

    stream?.videoEnable();
  }

  destroy(): void {
    const stream = this._stream$.value;

    this._stream$.next(null);
    this._stream$.complete();

    stream?.destroy();
  }
}
